import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { NavigationContext } from '@context/NavigationContext'
import { PredictionContext } from '@context/PredictionContext'
import { ConsentContext } from '@context/ConsentContext'
import { SurveyContext } from '@context/SurveyContext'
import { SurveyStep } from '@models/Survey/SurveyStep'
import { Choice, OneOfManyQuestion } from '@models/Survey'
import { AnswerType } from '@models/Survey/Answer'
import { TabbedPane } from '@components/Survey/TabbedPane/TabbedPane'
import { DefiDatagotchiCallout } from '@components/DefiDatagotchiCallout/DefiDatagotchiCallout'
import Markdown from '@components/Markdown/Markdown'
import { PredictionData } from '@models/Prediction/Prediction'
import { SurveyMode } from '@reducers/SurveyReducer'
import './Prediction.scss'
import { usePreventBackNavigation } from '@hooks/usePreventBackNavigation'
import { useEnsureSurveyCompleted } from '@hooks/useEnsureSurveyCompleted'
import { categoryUsesDsmpPrediction, getCategory } from '@utils/categoryFlow'
import { useGotchiCelebration } from '@context/GotchiCelebrationContext'

export function Prediction() {
  useEnsureSurveyCompleted()
  usePreventBackNavigation()
  const { t, i18n } = useTranslation('Prediction')
  const { setPrevHandler, setNextHandler } = useContext(NavigationContext)
  const { submitAnswers, setMode, setNextStep, survey } = useContext(SurveyContext)
  const {
    prediction,
    sandboxedResults,
    results,
    predictionVeracity,
    setPredictionVeracity,
    submitRectification: confirmPrediction,
  } = useContext(PredictionContext)
  const { playBouquet } = useGotchiCelebration()
  const { consentData, checkConsents } = useContext(ConsentContext)
  const [currentTarget, setCurrentTarget] = useState<undefined | PredictionData>(undefined)
  /** Last pointer position — used as firework anchor when Oui is selected. */
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null)
  const submitAttemptedRef = useRef(false)

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      lastPointerRef.current = { x: e.clientX, y: e.clientY }
    }
    window.addEventListener('pointerdown', onPointerDown, { capture: true })
    return () => window.removeEventListener('pointerdown', onPointerDown, { capture: true })
  }, [])

  useEffect(() => {
    if (!consentData) {
      void checkConsents()
    }
  }, [consentData, checkConsents])

  useEffect(() => {
    if (categoryUsesDsmpPrediction(getCategory(survey))) {
      setNextStep(SurveyStep.Results)
      return
    }
    setMode(SurveyMode.RESULTS)
    // in case of back navigation
    setNextStep(SurveyStep.Prediction)

    setPrevHandler(undefined)
    setNextHandler(undefined)

    return () => {
      setMode(SurveyMode.DEFAULT)
      setPrevHandler(undefined)
      setNextHandler(undefined)
    }
  }, [])

  useEffect(() => {
    if (categoryUsesDsmpPrediction(getCategory(survey)) || submitAttemptedRef.current) {
      return
    }
    if (!consentData?.visitorId || prediction) {
      return
    }
    submitAttemptedRef.current = true
    void submitAnswers()
  }, [consentData?.visitorId, prediction, survey, submitAnswers])

  useEffect(() => {
    setCurrentTarget(prediction)
  }, [prediction])

  const yesId = 'yes'
  const choices = [
    {
      id: yesId,
      text: `> ${t('choices.yes')}`,
    },
    {
      id: 'no',
      text: `> ${t('choices.no')}`,
    },
  ] as Choice[]
  const question = {
    choices,
  } as OneOfManyQuestion

  useEffect(() => {
    if (predictionVeracity !== undefined) {
      setNextHandler(() => handleNextAction())
    } else {
      setNextHandler(undefined)
    }
  }, [predictionVeracity])

  const handleSelection = useCallback(
    (answer?: AnswerType) => {
      if (answer === yesId) {
        playBouquet(lastPointerRef.current ?? undefined)
        return setPredictionVeracity(true)
      }

      return setPredictionVeracity(false)
    },
    [playBouquet, setPredictionVeracity],
  )

  const handleNextAction = useCallback(() => {
    if (predictionVeracity) {
      if (currentTarget) {
        confirmPrediction(currentTarget)
      }

      return setNextStep(SurveyStep.Probability)
    }
    return setNextStep(SurveyStep.Rectify)
  }, [currentTarget, predictionVeracity])

  const currentLocale = (i18n.language ?? 'fr').substring(0, 2)
  const predictionMarkdown = t('predictionQuestion', {
    target: `${currentTarget?.names?.full[currentLocale] ?? ''}`,
  })
  const tabs = [
    {
      text: t('navigation.title'),
    },
  ]
  return (
    <TabbedPane
      tabs={tabs}
      contentText={
        <DefiDatagotchiCallout className="Prediction-defiCallout">
          <Markdown content={predictionMarkdown} />
        </DefiDatagotchiCallout>
      }
      choices={question}
      onChoiceSelection={handleSelection}
      propClassName="Prediction"
      predictionResults={sandboxedResults ?? results}
    />
  )
}

export default Prediction
